import { FC, memo } from "react";

import { Tooltip } from "antd";
import classnames from "classnames";
import styled from "styled-components/macro";
import { arrayOrEmpty } from "utils";
import getColumnLabelWithContext, {
  getCompactFieldDisplayTitle,
  getFlatDisplayTitle
} from "utils/column/getColumnLabelWithContext";

import { useGlobalProductionSettings } from "hooks";

import { BaseButton } from "components/base";
import { doesTileMatchFocus, parseTileQualifications } from "components/groupBy/helpers";
import { parseForecastQualification } from "components/groupBy/helpers/qualifyForecastProperty";
import { IconSpinner } from "components/icons";

import { EntityKind } from "../../../models/entityKind";
import { useForecastFolderNameResolver } from "../hooks/useForecastFolderNameResolver";
import useTileColumns from "../hooks/useTileColumns";
import { GroupBySelection } from "../types";

type PresetFieldsT = {
  entityKind: EntityKind;
  onSelect: (selection: GroupBySelection) => void;
};

const PresetFields: FC<PresetFieldsT> = ({ onSelect, entityKind }) => {
  // state from store
  const globalSettings = useGlobalProductionSettings(entityKind);

  const { isLoading, tileColumns } = useTileColumns(entityKind);
  const { resolveForecastFolderName, isLoadingFolders } = useForecastFolderNameResolver();

  // classes
  const getButtonClasses = (tile: string) =>
    classnames({ isSelected: doesTileMatchFocus(tile, globalSettings.groupBy) });

  // The click commits the folder name, it does not merely show it, and nothing re-resolves it
  // afterwards. So a tile whose source is a user folder is not offered until the project's
  // folder list can name it — otherwise a click in that window pins the raw id on the axis,
  // and the header, legend and title read it back for the rest of the session.
  //
  // Held per panel rather than per tile, because the panel already has a spinner and a tile
  // that cannot be named cannot be labelled either. Held only when a tile actually needs the
  // list: the four built-in sources resolve synchronously, so a user with no custom-folder
  // tile never waits on this query.
  const isAwaitingFolderNames =
    isLoadingFolders &&
    arrayOrEmpty(tileColumns).some(({ tile }) => {
      const forecast = parseForecastQualification(tile);
      return (
        forecast.isQualified &&
        resolveForecastFolderName(forecast.forecastFolder) === undefined
      );
    });

  if (isLoading || isAwaitingFolderNames) return <StyledSpinner />;

  return (
    <Wrapper>
      {arrayOrEmpty(tileColumns).map(({ tile, column }) => {
        const dataType = column.dataType;
        const forecast = parseForecastQualification(tile);
        // A tile with no forecast still sends an empty name, so that it clears the source
        // the last tile set instead of inheriting it. A source the resolver still cannot name
        // here is a folder this project does not have — the in-flight case is held above — so
        // it falls back to its id, because an empty name would read as EVA Research.
        const forecastFolderName = forecast.isQualified
          ? resolveForecastFolderName(forecast.forecastFolder) ?? forecast.forecastFolder
          : "";
        // Undefined for a tile stored before ranges and production sources were kept, which
        // leaves that tile borrowing the session's values exactly as it does now.
        const { performanceDateRange, pdenSource } = parseTileQualifications(tile);
        const labels = getColumnLabelWithContext(
          column,
          !!globalSettings.normalizeBy,
          globalSettings.normalizeBy,
          forecast.isQualified,
          forecastFolderName,
          entityKind,
          performanceDateRange
        );

        // The category prefix alone fills a tile (EVA-339), so it's dropped from the button and
        // the full name moves to the hover. Tiles it doesn't shorten keep their old hover.
        const label = getCompactFieldDisplayTitle(column, labels, column.title);
        const fullTitle = getFlatDisplayTitle(column.property, labels, column.title);
        const showFullTitle = fullTitle !== label;
        const showDescription = !!column.tooltip && column.tooltip !== fullTitle;

        return (
          <Tooltip
            title={
              showFullTitle || showDescription ? (
                <>
                  {showFullTitle && <div>{fullTitle}</div>}
                  {showDescription && <div>{column.tooltip}</div>}
                </>
              ) : undefined
            }
            key={tile}
            mouseEnterDelay={0.5}
            mouseLeaveDelay={0}>
            <BaseButton
              className={getButtonClasses(tile)}
              appearance="none"
              data-testid={`focus-tile-${tile}`}
              onClick={() =>
                onSelect({
                  property: tile,
                  dataType,
                  options: {
                    isForecastToggleOn: forecast.isQualified,
                    selectedForecastFolderName: forecastFolderName,
                    performanceDateRange,
                    // Sent even when the tile has none, so the handler's fallback to the
                    // globally selected source is reached only by a tile that stored none.
                    // gDC is 0, so it is passed straight through rather than tested for
                    // truth, which would drop it into that same fallback.
                    pdenSource
                  }
                })
              }>
              {label}
            </BaseButton>
          </Tooltip>
        );
      })}
    </Wrapper>
  );
};

export default memo(PresetFields);

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 6px;
  padding: 10px 15px;
  overflow-y: overlay;
`;

const StyledSpinner = styled(IconSpinner)`
  margin: auto auto;
`;
